// Methodology entries, keyed by the signal they describe.
//
// Methodology is a property of the *signal*, not of the company: how a visit
// is detected from telco network events has nothing to do with how it is
// detected from a GPS panel. A country is served by one or more supply
// sources (see `supplySources` in concepts.ts), each of which carries a
// signal, so a country's applicable methodology falls out of its supply.
// That is why these entries are surfaced inside each country catalog rather
// than as a country-agnostic section: a reader in the USA should never be
// shown antenna-coverage methodology that does not apply to their data.
//
// Per-metric-model methodology (the `methodology` string on MetricModel)
// is a different, narrower thing and stays where it is.

/** The kind of underlying signal a methodology entry describes. */
export type Signal = "gps" | "telco"

export interface MethodologyEntry {
  /** URL slug. */
  id: string
  /** Display title. */
  title: string
  /** One-line description used in lists. */
  shortDescription: string
  /** Optional secondary headline shown on the detail page. */
  longDescription: string
  /** Optional markdown body rendered below the description on detail pages. */
  body?: string
  /** IDs of related methodology entries (rendered as cross-links). */
  relatedTo?: string[]
  /**
   * Signals this entry applies to. An entry is only shown in a country
   * catalog when that country has supply carrying one of these signals.
   */
  appliesTo: Signal[]
}

export const methodologyEntries: MethodologyEntry[] = [
  {
    id: "visit-assignment",
    title: "Visit assignment",
    shortDescription:
      "What counts as a visit, how staypoints are detected, and how positions are expressed as H3 hexagon distributions.",
    longDescription:
      "A visit is the presence of a device within a specific location for a sustained period, derived from raw antenna signals. Devices are not pinned to a single lat/lon, and each staypoint is expressed as a probability distribution across H3 resolution-10 hexagons, refined by contextual priors.",
    relatedTo: ["geolocation", "k-filtering"],
    appliesTo: ["telco"],
  },
  {
    id: "geolocation",
    title: "Geolocation",
    shortDescription:
      "Bayesian placement of devices within an antenna's coverage area, refined with contextual layers (land use, OSM, dwell models). Replaces the older population-weighted grid method.",
    longDescription:
      "The new geolocation model uses probabilistic techniques to estimate where within a coverage area a device is. Multiple layers (signal triangulation, contextual data such as OSM and land use, and behavioral models) combine into a probability field per destination, allowing realistic placement even in unpopulated areas.",
    relatedTo: ["visit-assignment", "home-and-work"],
    appliesTo: ["telco"],
  },
  {
    id: "home-and-work",
    title: "Home & work inference",
    shortDescription:
      "Estimating the likely home and work areas of visitor groups, always at area level and never an address, to enable resident / worker / visitor breakdowns, trade areas, and visitor-only scoping for Visit Length.",
    longDescription:
      "A likely home area and work area are inferred from activity patterns (home areas from sustained night-time presence, work areas from sustained day-time presence). \"Home\" here always means a statistical area: a neighborhood-sized zone, never an address, a building, or a household. These area anchors feed the resident / worker / visitor split in foot traffic, the visitor-only scope of visit length, and, most importantly, the home and work dimensions of trade areas.",
    body: `## What "home" means, and what it can never reveal

Pine59 datasets do not contain, and cannot be used to derive, where any individual lives or works. "Home area" is an aggregated statistical concept, protected by several complementary layers:

- **Area level only.** Home and work are inferred as coarse areas (a census block group, a postal area, a statistical tract), never a street address, a building, or a coordinate.
- **Aggregation only.** The inference is an intermediate input to aggregate statistics. What is published is always a group-level figure, such as "12% of this store's visitors have their home area in this ZIP code". No individual-level record exists in any output.
- **Aggregation at the source.** Depending on the signal, aggregation begins before the data reaches Pine59, so individual-level traces are not part of our pipeline.
- **Minimum-cohort suppression.** Any home-area statistic representing fewer than a minimum number of visitors is suppressed entirely (see *K filtering*), so small groups can never be singled out.
- **Rounding.** Published figures are rounded, so no output implies a precise individual observation.

Together these mean the question "where does this person live?" is not answerable from Pine59 data, by us or by anyone else. The only question the data answers is "roughly where does this *crowd* come from?", which is exactly what trade areas and visitor profiles are for.`,
    relatedTo: ["extrapolation", "k-filtering", "privacy-approach"],
    appliesTo: ["gps", "telco"],
  },
  {
    id: "extrapolation",
    title: "Extrapolation",
    shortDescription:
      "Scaling the observed sample of devices up to population-level visit estimates, so counts reflect real-world volume rather than sample size.",
    longDescription:
      "Only a fraction of the population is observed, so counts are scaled up to represent everyone. The scaling is based on market share: how many of an area's residents we observe, compared with the area's population. Devices are counted by their likely home area, and the market share is updated continuously, so the scaling follows the panel as it changes over time rather than relying on a fixed factor. Where a model produces the estimates, as for Foot Traffic on places, its output is also calibrated against ground-truth visitation data. The result reflects real-world visit volume rather than the size of the underlying sample.",
    body: `## Signal density

For GPS-based data, the scaling also corrects for signal density. Not all GPS sources observe devices continuously through the day: some only record a device's whereabouts at certain times, and this varies between sources and over time. A device that is observed for less of the day is seen at fewer places, even if its owner's behavior is unchanged. We therefore measure how much activity is recorded per device each day and correct for changes in it, so differences in reporting frequency do not read as differences in visits.`,
    relatedTo: ["home-and-work"],
    appliesTo: ["gps", "telco"],
  },
  {
    id: "trips-and-journeys",
    title: "Trips & journeys",
    shortDescription:
      "How sequences of visits are grouped into trips and journeys for Way and ODM data.",
    longDescription:
      "A trip is a pair of subsequent visits for the same device. A journey is one or more trips that together likely form one continuous route, grouped by a dynamic break parameter that allows longer dwells when cumulative distance grows. Mode of transport is inferred from speed and refined by path inference.",
    relatedTo: ["path-inference", "visit-assignment"],
    appliesTo: ["telco"],
  },
  {
    id: "path-inference",
    title: "Path inference",
    shortDescription:
      "Determining the most likely route a device took through the road network, required for Traffic data and refines mode-of-transport assignment.",
    longDescription:
      "Path inference uses the trip's origin, destination, and intermediate cell-tower pings against the OpenStreetMap-derived transport network to determine the most likely path. It distinguishes road from rail, subway, tram, and ferry travel and is what powers way-level Traffic counts.",
    body: `## The road network is OpenStreetMap

The network trips are matched onto is **OpenStreetMap (OSM)**, not a proprietary road graph. That has two practical consequences:

- **Counts are keyed to OSM way ids.** Every Traffic row carries the OSM \`way_id\` of the segment it describes, so results join directly to any OSM-derived network model you already run, with no vendor-specific segment mapping in between.
- **Segment granularity is OSM's.** A long street is split into several ways wherever OSM splits it, so counts are per OSM segment rather than per named street.

## Which roads are covered

We ingest the **classified through-road network**, meaning these OSM \`highway\` classes and the link roads that connect them:

| OSM class | What it is |
| --- | --- |
| \`motorway\`, \`motorway_link\` | Grade-separated highways and their ramps |
| \`trunk\`, \`trunk_link\` | Major national routes |
| \`primary\`, \`primary_link\` | Primary regional roads |
| \`secondary\`, \`secondary_link\` | Secondary regional roads |
| \`tertiary\`, \`tertiary_link\` | Local connecting roads |

Anything below tertiary is **not** included: no \`residential\`, \`unclassified\`, \`service\`, or \`living_street\`, and no pedestrian-only ways (\`footway\`, \`cycleway\`, \`path\`, \`track\`). If you need counts on a residential street or a footpath, Traffic will not have it.

Which classes actually appear varies with the country's road network. Estonia, for example, has no motorway-class roads at all.

Rail (\`rail\`, \`subway\`, \`tram\`, \`light_rail\`) and ferry routes are part of the same ingested network and are used to separate those modes from road travel, but only road segments carry published Traffic counts.`,
    relatedTo: ["trips-and-journeys"],
    appliesTo: ["telco"],
  },
  {
    id: "k-filtering",
    title: "K filtering",
    shortDescription:
      "No statistic is published unless it represents at least a minimum number of visitors. Why category counts may not sum to totals.",
    longDescription:
      "Any aggregate with fewer than a minimum number of contributing visitors (the k-anonymity threshold) is suppressed from output datasets. This is why summing visitor categories (resident / worker / non-resident-non-worker) often falls short of the total visit count: the categories are smaller bins more likely to dip below the threshold. The same applies to visit-length buckets and to hourly aggregates. The exact threshold depends on the country and the signal behind it.",
    relatedTo: [],
    appliesTo: ["telco"],
  },
  {
    id: "differential-privacy",
    title: "Differential privacy",
    shortDescription:
      "Small unbiased noise added to Trips and ODM aggregates so individual-level inference is impossible while statistical use is unaffected.",
    longDescription:
      "On Trips and ODM datasets we add a tiny amount of unbiased noise, typically plus or minus 1 to 2 trips on any given count. The noise is small enough that statistical use isn't affected, but large enough that the contribution of any single individual is statistically indistinguishable from the noise itself.",
    relatedTo: ["k-filtering"],
    appliesTo: ["telco"],
  },
  {
    id: "privacy-approach",
    title: "Privacy approach",
    shortDescription:
      "Pine59 products are built for statistical use only, and no output can be linked to an individual person.",
    longDescription:
      "Pine59 products are built for statistical use only. We never publish data that can be linked to an individual person, and we apply complementary technical mechanisms across our datasets to enforce that. Exactly which mechanisms apply, and with which parameters, depends on the country and the signal behind it, so see your country's methodology for the specifics.",
    body: `## What this means in practice

- **Aggregation only.** All outputs are aggregates over visitors, locations, and time periods. No individual device identifiers cross the boundary, and raw coordinates are never exposed; data is published at administrative levels or grids, not as individual location points.
- **Rounding.** Published figures are rounded (for example visit counts to whole people), so no output implies a precise individual observation.
- **Minimum thresholds, where they apply.** In some countries a statistic is only published when it represents enough visitors, and smaller aggregates are suppressed entirely. See *K filtering* for the detail and the consequences for category sums.
- **Noise, where it applies.** On some datasets (for example trips and origin-destination aggregates) a small unbiased random adjustment is added so any single individual's contribution is indistinguishable from background noise. See *Differential privacy*.

Which of these apply, and with which parameters, depends on the country and the signal behind it; each country's methodology page describes the specifics.`,
    relatedTo: ["k-filtering", "differential-privacy"],
    appliesTo: ["gps", "telco"],
  },
]

export function getMethodologyEntry(id: string): MethodologyEntry | undefined {
  return methodologyEntries.find((m) => m.id === id)
}
